<script setup>
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";

import ServiceSection from "@/pages/admin/ConstructorActions/tabs/components/ServiceSection.vue";
import {
  getServiceListWithResponseByCode,
  runChainService,
  getManualService,
  AUTO_FILLED_KEYS,
} from "@/entities/template";
import { useServiceQueueStore } from "@/store/serviceQueue";

import FieldBoolean from "@/features/dynamic-fields/ui/FieldBoolean.vue";
import FieldString from "@/features/dynamic-fields/ui/FieldString.vue";
import FieldDecimal from "@/features/dynamic-fields/ui/FieldDecimal.vue";
import FieldInt from "@/features/dynamic-fields/ui/FieldInt.vue";
import FieldArray from "@/features/dynamic-fields/ui/FieldArray.vue";
import FieldDate from "@/features/dynamic-fields/ui/FieldDate.vue";
import FieldJson from "@/features/dynamic-fields/ui/FieldJson.vue";
import FieldEnum from "@/features/dynamic-fields/ui/FieldEnum.vue";
import FloatingInput from "@/shared/ui/custom/input/FloatingInput.vue";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import { EXPIRATION_DATE_KEY } from "@/features/notarial-actions/composables/useEndDateField";

import {
  normalizeRuntimeResponse as normalizeRuntimeResponseShared,
  summarizeArrayChoices,
} from "@/shared/serviceResponse";
import NotariesInfo from "@/shared/ui/custom/info/NotariesInfo.vue";
import InternalServiceList from "@/pages/admin/home/notarial_actions/components/internal_databeses/InternalServiceList.vue";
import Button from "@/shared/ui/button/Button.vue";
import { Spinner } from "@/shared/ui/spinner";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import CheckInfoBlock from "@/pages/admin/home/notarial_actions/components/CheckInfoBlock.vue";
import {
  fetcherEvents,
  showServiceNotification,
} from "@/shared/lib/centrifuge";
import { generateRequestUuid } from "@/shared/lib/uuid";
import { toast } from "@/shared/store/alertStore";

const props = defineProps({
  uuid: {
    type: [String, Number],
    default: null,
  },
  form: {
    type: Object,
    default: () => ({}),
  },
  execution: {
    type: Object,
    default: () => null,
  },
  notariesInfo: {
    type: Object,
    default: () => null,
  },
  activeStepCode: {
    type: String,
    default: null,
  },
  template: {
    type: Object,
    default: () => ({}),
  },
  selectedParticipant: {
    type: Object,
    default: () => ({}),
  },
  participant_type: {
    type: String,
    default: null,
  },
  inheritanceId: {
    type: String,
    default: "",
  },
  showInheritanceServices: {
    type: Boolean,
    default: false,
  },
  checkResults: {
    type: Object,
    default: null,
  },
  isTestamentAuthor: {
    type: Boolean,
    default: false,
  },
  statementNotFound: {
    type: Boolean,
    default: false,
  },
  existingCaseFound: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "service-response-changed",
  "service-response-error",
  "service-loading-start",
  "service-loading-end",
  "dash:array-selected",
  "insert:fields",
  "insert:field",
  "show-notary-data",
  "attach-testament",
  "open-testament-document",
  "show-existing-case-info",
  "show-statement-info",
  "export-report",
]);

const { t, locale } = useI18n();
const serviceQueue = useServiceQueueStore();

// Ключи, которые не показываем в «Ручном вводе»: они заполняются автоматически
const HIDE_KEYS = [EXPIRATION_DATE_KEY, "collected_amount", "number"];

const serviceLoading = ref(false);
const activeService = ref(null);
const selectedService = ref(null);
const serviceResponseData = ref(null);
const dynamicForm = ref({});
const searching = ref(false);
const searchCompleted = ref(false);
const pendingServiceCode = ref(null);
const pendingRequestUuid = ref(null);
const pendingParticipantUuid = ref(null);
const manualInputFields = ref({});

let unsubscribeWs = null;
let requestStartedAt = 0;
const MIN_SKELETON_MS = 1000;

function resolveSchemaType(schema) {
  let type = schema.type;
  if (!type && Array.isArray(schema.anyOf)) {
    type = schema.anyOf.find((s) => s.type && s.type !== "null")?.type;
  }
  return type?.toLowerCase();
}

const dynamicRequestFields = computed(() => {
  let request = activeService.value?.request;
  if (typeof request === "string") {
    try {
      request = JSON.parse(request);
    } catch {
      request = null;
    }
  }
  if (!request?.properties) return [];

  const required = request.required || [];

  return Object.entries(request.properties).map(([key, schema]) => ({
    key,
    title: schema.title ?? key,
    type: resolveSchemaType(schema),
    enums: schema.enum,
    required: required.includes(key),
    schema,
  }));
});

const currentSteps = computed(() => {
  return props.execution?.steps_entity_types[props.participant_type];
});

const currentStep = computed(() => {
  const ex = props.execution;
  if (!ex || !activeService.value?.code) return null;
  return (
    currentSteps.value?.find((s) => s.code === activeService.value.code) || null
  );
});

const queueStepStatus = computed(() => {
  const pUuid = props.selectedParticipant?.uuid;
  const code = activeService.value?.code || props.activeStepCode;
  if (!pUuid || !code) return null;
  return serviceQueue.getStepStatus(pUuid, code);
});

const isQueueRunning = computed(() => queueStepStatus.value === "running");

const isSearching = computed(() => searching.value || isQueueRunning.value);

const hasManualInputValues = computed(() =>
  Object.values(manualInputFields.value || {}).some(
    (v) => v !== "" && v !== null && v !== undefined
  )
);

function insertAllManualFieldsToDocument() {
  const entries = Object.entries(manualInputFields.value || {});
  if (!entries.length) return;
  const fields = entries
    .filter(
      ([, value]) => value !== "" && value !== null && value !== undefined
    )
    .map(([key, value]) => ({
      key,
      value,
      label: props.template?.fields?.[key]?.name || key,
      isCustom: true,
    }));
  if (!fields.length) return;
  emit("insert:fields", fields);
}

watch(
  () => props.activeStepCode,
  (code) => {
    if (!code) {
      activeService.value = null;
      selectedService.value = null;
      dynamicForm.value = {};
      serviceResponseData.value = null;
      searchCompleted.value = false;
      return;
    }

    loadServiceByCode(code);
  },
  { immediate: true }
);

watch(
  activeService,
  (service) => {
    if (service?.code === "manual_input") {
      initManualInputFields();
    }
  },
  { immediate: true }
);

watch(
  () => props.selectedParticipant,
  (participant) => {
    const id = participant?.identifier;
    if (!id) return;
    AUTO_FILLED_KEYS.forEach((key) => {
      if (checkHasKeyDynamicForm(key)) {
        dynamicForm.value[key] = id;
      }
    });
  },
  { immediate: true }
);

watch(
  () => props.selectedParticipant?.uuid,
  (newUuid, oldUuid) => {
    if (!newUuid || newUuid === oldUuid) return;
    const code = props.activeStepCode;
    if (!code || code === "manual_input" || code === "show-check-info") return;
    const savedData = props.selectedParticipant?.service_fields?.[code];
    const queueData = serviceQueue.getStepData(newUuid, code);
    const queueStatus = serviceQueue.getStepStatus(newUuid, code);
    if (queueData && queueStatus === "success") {
      serviceResponseData.value = queueData;
      searchCompleted.value = true;
    } else if (queueStatus === "error") {
      serviceResponseData.value = null;
      searchCompleted.value = true;
    } else if (savedData && Object.keys(savedData).length > 0) {
      serviceResponseData.value = savedData;
      searchCompleted.value = true;
    } else {
      serviceResponseData.value = null;
      searchCompleted.value = false;
    }
  }
);

// Живая синхронизация вьюера с очередью: когда очередь/запрос по активному
// сервису завершается, обновляем данные сразу, не дожидаясь повторного клика.
watch(queueStepStatus, (status, prevStatus) => {
  if (status === prevStatus) return;
  const pUuid = props.selectedParticipant?.uuid;
  const code = activeService.value?.code;
  if (!pUuid || !code) return;
  if (code === "manual_input" || code === "show-check-info") return;
  if (status === "success") {
    const data = serviceQueue.getStepData(pUuid, code);
    if (data) {
      serviceResponseData.value = data;
      searchCompleted.value = true;
    }
  } else if (status === "error") {
    serviceResponseData.value = null;
    searchCompleted.value = true;
  } else if (status === "running") {
    // Запрос пошёл заново — очищаем старые данные до прихода нового ответа.
    serviceResponseData.value = null;
    searchCompleted.value = false;
  }
});

async function loadServiceByCode(code) {
  serviceResponseData.value = null;
  searchCompleted.value = false;
  searching.value = false;
  pendingServiceCode.value = null;
  pendingRequestUuid.value = null;
  pendingParticipantUuid.value = null;
  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }

  if (code === "manual_input") {
    activeService.value = { code: "manual_input", name: "Ручной ввод" };
    initManualInputFields();
  } else if (code === "show-check-info") {
    activeService.value = {
      code: "show-check-info",
      name: "Информация проверки",
    };
  } else if (getManualService(code)) {
    const manualService = getManualService(code);
    activeService.value = {
      code: manualService.code,
      name: manualService.name,
    };
    selectedService.value = {
      name: manualService.name,
      code: manualService.code,
      response: manualService.response,
    };
    const savedData = props.selectedParticipant?.service_fields?.[code];
    if (savedData && Object.keys(savedData).length > 0) {
      serviceResponseData.value = savedData;
      searchCompleted.value = true;
    }
  } else {
    try {
      serviceLoading.value = true;
      const data = await getServiceListWithResponseByCode(code);
      if (data?.success && data.payload) {
        activeService.value = data.payload;
        dynamicForm.value = initDynamicForm(
          data.payload.request?.properties || {}
        );
        searchCompleted.value = false;

        selectedService.value = {
          name: data.payload.name,
          code: data.payload.code,
          response: data.payload.response,
        };
      } else {
        activeService.value = null;
        selectedService.value = null;
        dynamicForm.value = {};
      }
    } catch (e) {
      console.error("loadServiceByCode error:", e);
      activeService.value = null;
      selectedService.value = null;
      dynamicForm.value = {};
    } finally {
      serviceLoading.value = false;
    }

    if (props.selectedParticipant?.identifier) {
      AUTO_FILLED_KEYS.forEach((key) => {
        if (checkHasKeyDynamicForm(key)) {
          dynamicForm.value[key] = props.selectedParticipant.identifier;
        }
      });
    }

    const pUuid = props.selectedParticipant?.uuid;
    if (pUuid) {
      const queueData = serviceQueue.getStepData(pUuid, code);
      const queueStatus = serviceQueue.getStepStatus(pUuid, code);
      if (queueData && queueStatus === "success") {
        serviceResponseData.value = queueData;
        searchCompleted.value = true;
      } else if (queueStatus === "error") {
        serviceResponseData.value = null;
        searchCompleted.value = true;
      } else {
        const savedData = props.selectedParticipant?.service_fields?.[code];
        if (savedData && Object.keys(savedData).length > 0) {
          serviceResponseData.value = savedData;
          searchCompleted.value = true;
        }
      }
    }
  }
}

function initDynamicForm(properties = {}) {
  return Object.fromEntries(
    Object.entries(properties).map(([key, schema]) => [
      key,
      schema.default ?? (schema.type === "boolean" ? false : ""),
    ])
  );
}

function getFieldComponent(field) {
  if (field.enums?.length) return FieldEnum;

  const map = {
    boolean: FieldBoolean,
    string: FieldString,
    number: FieldDecimal,
    integer: FieldInt,
    array: FieldArray,
    datetime: FieldDate,
    object: FieldJson,
  };

  return map[field.type] || FieldString;
}

function prepareServicePayload() {
  const payload = { ...dynamicForm.value };
  const id = props.selectedParticipant?.identifier;
  if (id) {
    AUTO_FILLED_KEYS.forEach((key) => {
      if (key in payload && !payload[key]) payload[key] = id;
    });
  }
  return payload;
}

async function validateForms() {
  const missing = dynamicRequestFields.value.filter((f) => {
    if (!f.required) return false;
    const v = dynamicForm.value[f.key];
    return v === "" || v === null || v === undefined;
  });
  if (missing.length) {
    toast.error(
      t("Заполните обязательные поля") +
        ": " +
        missing.map((f) => f.title).join(", ")
    );
    return false;
  }
  return true;
}

function loadingPayload() {
  return {
    service_code: activeService.value?.code,
    participant_type_code: props.selectedParticipant?.participant_type_code,
  };
}

function recordQueueResult(
  status,
  data = null,
  code = activeService.value?.code,
  participantUuid = props.selectedParticipant?.uuid
) {
  if (!participantUuid || !code) return;
  serviceQueue.recordStandaloneResult({
    participantUuid,
    stepCode: code,
    status,
    data,
  });
}

async function getServiceResponse() {
  if (!(await validateForms())) return;

  searching.value = true;
  searchCompleted.value = true;

  const startPayload = loadingPayload();
  requestStartedAt = Date.now();
  emit("service-loading-start", startPayload);

  const reqServiceCode = activeService.value?.code;
  const reqParticipantUuid = props.selectedParticipant?.uuid;

  try {
    const execution = props.execution;
    const step = currentStep.value;

    if (!execution || !step) {
      emit("service-loading-end", startPayload);
      return;
    }

    // Подписываемся ДО запуска — чтобы не пропустить быстрый WS-ответ (гонка).
    const clientRequestUuid = generateRequestUuid();
    pendingServiceCode.value = reqServiceCode;
    pendingRequestUuid.value = clientRequestUuid;
    pendingParticipantUuid.value = reqParticipantUuid;
    recordQueueResult("running", null, reqServiceCode, reqParticipantUuid);
    unsubscribeWs = fetcherEvents.on(handleWsResponse);

    const result = await runChainService(
      execution.uuid,
      step.uuid,
      props.uuid,
      { ...prepareServicePayload(), request_uuid: clientRequestUuid }
    );

    if (result?.success && result.payload?.run) {
      console.log(
        "service:",
        activeService.value?.code,
        "run:",
        result.payload.run
      );
      // НЕ снимаем searching — спиннер крутится пока ждём WS
      return;
    }

    pendingServiceCode.value = null;
    pendingRequestUuid.value = null;
    if (unsubscribeWs) {
      unsubscribeWs();
      unsubscribeWs = null;
    }
    recordQueueResult("error", null, reqServiceCode, reqParticipantUuid);
    emit("service-response-error");
    emit("service-loading-end", startPayload);
  } catch (e) {
    console.error("getServiceResponse error:", e);
    pendingServiceCode.value = null;
    pendingRequestUuid.value = null;
    if (unsubscribeWs) {
      unsubscribeWs();
      unsubscribeWs = null;
    }
    recordQueueResult("error", null, reqServiceCode, reqParticipantUuid);
    emit("service-response-error");
    emit("service-loading-end", startPayload);
  } finally {
    if (!pendingServiceCode.value) {
      setTimeout(() => (searching.value = false), 800);
    }
  }
}

function initManualInputFields() {
  const templateFields = props.template?.fields;
  if (!templateFields) return;

  manualInputFields.value = Object.fromEntries(
    Object.entries(templateFields)
      .filter(([key]) => !HIDE_KEYS.includes(key))
      .map(([key, field]) => [
        key,
        props.form?.fields?.[key]?.value ?? field.value ?? "",
      ])
  );
}

watch(
  manualInputFields,
  (val) => {
    if (!val || !props.form) return;
    if (!props.form.fields) props.form.fields = {};

    Object.entries(val).forEach(([key, value]) => {
      const existing = props.form.fields[key] || {};
      const templateField = props.template?.fields?.[key] || {};
      props.form.fields[key] = {
        ...templateField,
        ...existing,
        name: existing.name || templateField.name || key,
        value,
      };
    });
  },
  { deep: true }
);

function checkHasKeyDynamicForm(key) {
  return Object.keys(dynamicForm.value).includes(key);
}

function normalizeRuntimeResponse(payload) {
  return normalizeRuntimeResponseShared(payload, {
    locale: locale.value || "ru",
    participantTypeCode: props.selectedParticipant?.participant_type_code,
    serviceCode: activeService.value?.code,
  });
}

onBeforeUnmount(() => {
  if (pendingServiceCode.value) {
    emit("service-loading-end", {
      service_code: pendingServiceCode.value,
      participant_type_code: props.selectedParticipant?.participant_type_code,
    });
  }
  pendingServiceCode.value = null;
  pendingRequestUuid.value = null;
  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }
});

function handleArrayItemSelected(payload) {
  if (payload?.fields.length) {
    emit("insert:fields", payload.fields);
  }
  // После выбора элемента массива — заполнить дефисом пустые/отсутствующие
  // ключи этого массива (выбор сделан, данные финализированы).
  if (payload?.arrayPath) {
    emit("dash:array-selected", {
      service_code: payload.service_code,
      participant_type_code: payload.participant_type_code,
      array_path: payload.arrayPath,
    });
  }
}

function handleWsResponse(data) {
  if (!pendingServiceCode.value) {
    console.error(
      "[WS] ПРОПУСК: pendingServiceCode пустой — ответ уже обработан или не ожидался"
    );
    return;
  }
  if (data?.type !== "FETCHER") {
    console.warn(
      "[WS] ПРОПУСК: тип сообщения не FETCHER, получен:",
      data?.type
    );
    return;
  }

  const wsPayload = data?.payload;
  if (!wsPayload || wsPayload.service_code !== pendingServiceCode.value) {
    console.error(
      "[WS] ПРОПУСК: код сервиса не совпадает. Пришёл:",
      wsPayload?.service_code,
      "| Ожидался:",
      pendingServiceCode.value
    );
    return;
  }
  if (
    pendingRequestUuid.value &&
    data.request_uuid !== pendingRequestUuid.value
  ) {
    console.error(
      "[WS] ПРОПУСК: uuid запроса не совпадает. Пришёл:",
      data.request_uuid,
      "| Ожидался:",
      pendingRequestUuid.value
    );
    return;
  }

  // PENDING — ещё обрабатывается, продолжаем ждать
  if (wsPayload.status === "PENDING") {
    console.warn("[WS] PENDING. service:", wsPayload.service_code);
    return;
  }

  // NOTIFICATION — ожидание подтверждения клиента (КДП), loading продолжается
  if (wsPayload.status === "NOTIFICATION") {
    showServiceNotification(wsPayload);
    return;
  }

  const finishedServiceCode = pendingServiceCode.value;
  const finishedParticipantUuid = pendingParticipantUuid.value;
  pendingServiceCode.value = null;
  pendingRequestUuid.value = null;
  pendingParticipantUuid.value = null;

  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }

  const endPayload = {
    service_code: finishedServiceCode,
    participant_type_code: props.selectedParticipant?.participant_type_code,
  };

  const elapsed = Date.now() - (requestStartedAt || Date.now());
  const remaining = Math.max(0, MIN_SKELETON_MS - elapsed);

  setTimeout(() => {
    if (
      wsPayload.status === "FINISHED" &&
      wsPayload.data &&
      Object.keys(wsPayload.data).length > 0
    ) {
      serviceResponseData.value = wsPayload.data;
      recordQueueResult(
        "success",
        wsPayload.data,
        finishedServiceCode,
        finishedParticipantUuid
      );
      emit("service-response-changed", wsPayload.data);

      const fields = normalizeRuntimeResponse(wsPayload.data);
      if (fields.length) {
        emit("insert:fields", fields);
      } else {
        console.error(
          "[WS] Нет полей для вставки — normalizeRuntimeResponse вернул пустой массив"
        );
      }

      // Массивы с несколькими элементами ждут выбора пользователя — их mention
      // не должны заполняться дефисом до выбора.
      endPayload.array_paths = summarizeArrayChoices(wsPayload.data).multi.map(
        (m) => m.path
      );

      toast.success(t("Данные получены"), {
        description: new Date().toLocaleTimeString(),
        duration: 3000,
      });
    } else {
      recordQueueResult(
        "error",
        null,
        finishedServiceCode,
        finishedParticipantUuid
      );
      emit("service-response-error");

      const errorMsg =
        wsPayload?.error_description ||
        t("Не удалось получить данные от сервиса");
      toast.error(errorMsg, {
        duration: 5000,
      });
    }

    emit("service-loading-end", endPayload);
    searching.value = false;
  }, remaining);
}
</script>

<template>
  <div class="w-full flex flex-col">
    <ServiceSection
      v-if="activeStepCode === 'manual_input'"
      title="Ручной ввод"
      :selectedService="activeService"
      :show-action="true"
      :type="'show-service-response-viewer'"
      :selectedParticipant="selectedParticipant"
    >
      <template #action>
        <div>
          <div class="flex flex-col w-full gap-2.5">
            <div
              v-for="(value, key) in manualInputFields"
              :key="key"
              class="w-full"
            >
              <FloatingInput
                v-model="manualInputFields[key]"
                :key-field="key"
                :label="template.fields[key]?.name || 'Введите значение'"
                class="w-full"
                @insert:field="$emit('insert:field', $event)"
              />
            </div>

            <Button
              v-if="Object.keys(manualInputFields).length"
              variant="outline"
              class="w-full mt-2 flex items-center justify-center"
              :disabled="!hasManualInputValues"
              @click="insertAllManualFieldsToDocument"
            >
              <SvgIcon name="huge-plus" class="w-3.5 h-3.5" />
              {{ $t("Добавить в документ") }}
            </Button>
          </div>
        </div>
      </template>
    </ServiceSection>

    <ServiceSection
      v-else-if="activeStepCode === 'internal_databases'"
      title="Внутренние базы"
      :selectedService="activeService"
      :show-action="true"
      :type="'show-service-response-viewer'"
      :selectedParticipant="selectedParticipant"
    >
      <template #action>
        <div class="flex flex-col gap-6">
          <InternalServiceList
            :selectedParticipant="selectedParticipant"
            :selectedService="activeService"
            :notaries-info="notariesInfo"
            :form="form"
            :inheritanceId="inheritanceId"
            :showInheritanceServices="showInheritanceServices"
          />
          <NotariesInfo :notaries-info="notariesInfo" />
        </div>
      </template>
    </ServiceSection>

    <ServiceSection
      v-else-if="activeStepCode === 'show-check-info' && inheritanceId"
      title="Информация о проверке"
      :selectedService="activeService"
      :show-action="true"
      :type="'show-service-response-viewer'"
      :selectedParticipant="selectedParticipant"
    >
      <template #action>
        <CheckInfoBlock
          :statementNotFound="statementNotFound"
          :existingCaseFound="existingCaseFound"
          :checkResults="checkResults"
          :isTestamentAuthor="isTestamentAuthor"
          @export-report="$emit('export-report')"
          @show-existing-case-info="$emit('show-existing-case-info')"
          @show-statement-info="$emit('show-statement-info')"
          @show-notary-data="$emit('show-notary-data')"
          @attach-testament="$emit('attach-testament')"
          @open-testament-document="$emit('open-testament-document')"
        />
      </template>
    </ServiceSection>

    <ServiceSection
      v-else
      :title="activeService ? activeService.name : 'Магические вставки'"
      :activeService="activeService"
      :selectedService="selectedService"
      :serviceResponseData="serviceResponseData"
      :searching="isSearching"
      :search-complected="searchCompleted"
      :show-action="true"
      :selectedParticipant="selectedParticipant"
      type="show-service-response-viewer"
      @insert:field="$emit('insert:field', $event)"
      @select:array-item="handleArrayItemSelected"
    >
      <template #action>
        <div
          v-if="serviceLoading"
          class="flex justify-center items-center py-8"
        >
          <Spinner size="sm" />
        </div>
        <div v-else-if="activeService">
          <div class="flex flex-col w-full">
            <div v-for="f in dynamicRequestFields" :key="f.key">
              <template v-if="f.type !== 'boolean'">
                <FormItem :float="f.type !== 'boolean'">
                  <FormLabel>
                    {{ f.title }}{{ f.required ? " *" : "" }}
                  </FormLabel>
                  <FormControl>
                    <component
                      :is="getFieldComponent(f)"
                      v-model="dynamicForm[f.key]"
                      :item="{ ...f, value: dynamicForm[f.key] }"
                      :disabled="
                        AUTO_FILLED_KEYS.includes(f.key) && !!dynamicForm[f.key]
                      "
                    />
                  </FormControl>
                </FormItem>
              </template>
            </div>
          </div>

          <Button
            @click="getServiceResponse"
            class="w-full flex items-center justify-center"
            :disabled="isSearching"
            variant="outline"
          >
            <template v-if="isQueueRunning">
              <Spinner size="sm" class="mr-2" />
              {{ $t("В очереди...") }}
            </template>
            <template v-else-if="searching">
              <Spinner size="sm" class="mr-2" />
              {{ $t("Поиск...") }}
            </template>
            <template v-else>
              {{ $t("Запросить данные") }}
            </template>
          </Button>
        </div>
      </template>
    </ServiceSection>
  </div>
</template>
